import { useMutation } from '@apollo/client';
import {
  DateTimeFormatter,
  toast,
  AccessControl,
  useIsAuthorisedHook,
  SecureText,
  useProduct,
  COMMON_CONSTANTS
} from '@nile/components';
import { RMMClient } from '@nile/graphql';
import { UPDATE_ASSET } from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import ComponentResolver from '@nile/rmm/src/modules/common/components/sub-pages/component-resolver';
import DynamicField from '@nile/rmm/src/modules/common/components/sub-pages/dynamic-field';
import {
  CustomField,
  NonMonitoredAssetInfoParent
} from '@nile/rmm/src/modules/it-documentation/components/sub-pages/non-monitored-asset-info/styles';
import {
  Heading,
  Popover,
  Button,
  ButtonGroup,
  TextStyle
} from '@superops/ui-design-system';
import { clone, cloneDeep, forOwn, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import MaskedCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/masked-cell';
import { CUSTOM_FIELDS } from '@nile/rmm/src/constants';
import RichTextField from '@nile/client_management/src/Components/fields/RichTextEditorField';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';

function NonMonitoredAssetInfo(props) {
  let currentFieldIndex = null;
  let isRichTextEditorField = false;
  const {
    fieldObj,
    assetClass,
    assetName,
    client,
    site,
    isClientPortal,
    entityName,
    isRunbookAction = false
  } = props;
  // const { assetId, classId } = props?.match?.params || props?.data;
  const allowItDocumentRichTextEdit =
    !isRunbookAction && useIsAuthorisedHook('itDocumentation.edit');
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  if (fieldObj.field.type === 'custom') {
    currentFieldIndex = assetClass.customFields.findIndex((field) => {
      return fieldObj.field.fieldProps.id === field.id;
    });
    isRichTextEditorField =
      assetClass?.customFields[currentFieldIndex]?.fieldType ===
      CUSTOM_FIELDS.RICH_TEXT;
  }

  const [customFields, setCustomFields] = useState(props.customFields || {});

  const [updatedCustomFields, setUpdatedCustomFields] = useState(
    props.customFields || {}
  );

  const [popoverActive, setPopoverActive] = useState(false);

  const [selectedClient, setSelectedClient] = useState(client || null);

  const [selectedSite, setSelectedSite] = useState(site || null);

  const [updatedDynamicFieldsData, setUpdatedDynamicFieldsData] = useState({
    client,
    site
  });

  const [assetInfo, setAssetInfo] = useState({});

  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
  });

  useEffect(() => {
    setUpdatedCustomFields(props.customFields || {});
  }, [props.customFields]);

  useEffect(() => {
    setUpdatedDynamicFieldsData({
      client,
      site
    });
  }, [client, site]);

  useEffect(() => {
    setSelectedClient(client);
  }, [client]);

  useEffect(() => {
    setSelectedSite(site);
  }, [site]);

  useEffect(() => {
    if (!isEmpty(props.assetInfo)) {
      setAssetInfo(props.assetInfo);
    }
  }, [props.assetInfo]);

  useEffect(() => {
    if (!isEmpty(props.customFields)) {
      setCustomFields(props.customFields);
    }
  }, [props.customFields]);

  const activator = (
    <AccessControl
      roleToCheck="itDocumentation.edit"
      render={
        <Button plainSecondary onClick={() => togglePopoverActive()}>
          <SVGIcon name="Edit" />
        </Button>
      }
    />
  );

  const [editAsset] = useMutation(UPDATE_ASSET, {
    client: RMMClient,
    onCompleted(res) {
      if (res) {
        toast('success', 'rmm.assetManagement.assetUpdate.successMsg');
        togglePopoverActive();
      }
    }
  });

  const editNonMonitoredAsset = (data) => {
    const { params } = props.match;
    editAsset({
      input: {
        assetId: params?.assetId || params?.id,
        input: data,
        isMSPProduct
      },
      variables: {
        assetId: params?.assetId || params?.id,
        input: data,
        isMSPProduct
      }
    }).then((res) => {
      if (res?.data?.updateAsset) {
        props.updatedCustomFields(updatedCustomFields);
        props.refetchAssetDetails();
        togglePopoverActive();
      }
    });
  };

  const getQueryDefaultValue = (columnName: string) => {
    const obj = {
      assetClass: {
        classId: props.match.params?.classId || assetClass.classId
      },
      name: assetName
    };
    if (!['client', 'site'].includes(columnName)) {
      return obj;
    }
    if (updatedDynamicFieldsData?.client) {
      obj['client'] = isEmpty(updatedDynamicFieldsData.client.accountId)
        ? null
        : {
            accountId: updatedDynamicFieldsData.client.accountId
          };
      obj['site'] = null;
    }

    if (updatedDynamicFieldsData?.site) {
      obj['site'] = isEmpty(updatedDynamicFieldsData.site.id)
        ? null
        : {
            id: updatedDynamicFieldsData.site.id
          };
    }

    return obj;
  };

  const handleSave = (columnName: string, value = null) => {
    const queryValue = clone(getQueryDefaultValue(columnName));
    if (columnName) {
      const obj = {
        ...queryValue,
        customFields: {
          [columnName]: isEmpty(value) ? updatedCustomFields[columnName] : value
        }
      };
      editNonMonitoredAsset(obj);
    } else {
      editNonMonitoredAsset(queryValue);
    }
  };

  const handleChange = (fieldName, value) => {
    const clonedCustomFields = cloneDeep(updatedCustomFields);
    clonedCustomFields[fieldName] = value;
    setUpdatedCustomFields(clonedCustomFields);
  };

  const handleDynamicQueryFieldChange = (value) => {
    let clonedUpdatedDynamicFieldsData = cloneDeep(updatedDynamicFieldsData);
    if (value.fieldName === 'client') {
      clonedUpdatedDynamicFieldsData['site'] = null;
    }
    clonedUpdatedDynamicFieldsData[value.fieldName] = value;
    setUpdatedDynamicFieldsData(clonedUpdatedDynamicFieldsData);
  };

  /**
   *
   * Formatting values
   * @param {any} value
   * @param {string} columnName
   * @public
   */
  const getProcessedValue = (value, columnName) => {
    if (
      columnName.includes('pwd') ||
      columnName === 'serialNumber' ||
      columnName === 'password'
    )
      return <MaskedCell maskedData={value} />;
    if (columnName.includes('dt')) {
      return <DateTimeFormatter type="dateTime" date={value} />;
    }
    if (columnName.includes('date')) {
      return <DateTimeFormatter type="date" date={value} />;
    }
    if (columnName.includes('num') || columnName.includes('dec')) {
      return value.toString();
    }
    if (
      (columnName.includes('check') || columnName.includes('select')) &&
      typeof value === 'object'
    ) {
      return value.join(',');
    } else {
      return value;
    }
  };

  const getDynamicQueryDefaultValue = (field) => {
    switch (field.name) {
      case 'client':
        return selectedClient?.name;
      case 'site':
        return selectedSite?.name;
      default:
        break;
    }
  };

  const getDynamicFieldConfig = (field) => {
    switch (field.name) {
      case 'site':
        return {
          dependentFieldId: selectedClient?.accountId || null,
          dependentFieldName: 'client'
        };
      default:
        return {};
    }
  };

  const renderDynamicQueryFields = (field) => {
    return (
      <div className="flex-row flex-align-center">
        <div className="flex-row flex-align-center">
          {getDynamicQueryDefaultValue(field) || 'NA'}
          {allowItDocumentRichTextEdit && (
            <div className="popover-parent">
              <Popover activator={activator} active={popoverActive}>
                <CustomField>
                  <DynamicField
                    isClearable
                    field={field}
                    value={getDynamicQueryDefaultValue(field)}
                    config={isMSPProduct ? getDynamicFieldConfig(field) : {}}
                    handleChange={(value) => {
                      handleDynamicQueryFieldChange(value);
                    }}
                  />
                  <ButtonGroup spacing="tight">
                    <Button onClick={togglePopoverActive} size="slim">
                      <FormattedMessage id="common.cancel" />
                    </Button>
                    <Button
                      primary
                      onClick={() => handleSave(field?.name)}
                      size="slim"
                    >
                      <FormattedMessage id="common.save" />
                    </Button>
                  </ButtonGroup>
                </CustomField>
              </Popover>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDefaultFieldsFromAssetInfo = (fieldIndex) => {
    return (
      <div>
        {!isEmpty(assetInfo)
          ? getProcessedValue(
              assetInfo[assetClass.assetDefaultFields[fieldIndex].name],
              assetClass.assetDefaultFields[fieldIndex].name
            )
          : 'NA'}
      </div>
    );
  };

  const renderDefaultFieldsFromAssetDefaultFields = (fieldIndex) => {
    return (
      <div>
        {!isEmpty(
          customFields[
            assetClass.assetDefaultFields[fieldIndex]?.name
          ]?.toString()
        )
          ? getProcessedValue(
              customFields[assetClass.assetDefaultFields[fieldIndex]?.name],
              assetClass.assetDefaultFields[fieldIndex]?.name
            )
          : 'NA'}
      </div>
    );
  };

  const renderAssetDefaultField = (fieldIndex) => {
    return (
      <div className="flex-row flex-align-center">
        <div className="flex-row flex-align-center">
          {!isEmpty(assetInfo)
            ? renderDefaultFieldsFromAssetInfo(fieldIndex)
            : renderDefaultFieldsFromAssetDefaultFields(fieldIndex)}
        </div>
        {assetClass.assetDefaultFields[fieldIndex]?.readOnly === false &&
        allowItDocumentRichTextEdit ? (
          <div className="popover-parent">
            <Popover activator={activator} active={popoverActive}>
              <CustomField>
                <ComponentResolver
                  attribute={assetClass.assetDefaultFields[fieldIndex]}
                  handleChange={(value) => {
                    handleChange(
                      assetClass.assetDefaultFields[fieldIndex].name,
                      value
                    );
                  }}
                  value={
                    customFields[assetClass.assetDefaultFields[fieldIndex].name]
                  }
                ></ComponentResolver>
                <ButtonGroup spacing="tight">
                  <Button onClick={togglePopoverActive} size="slim">
                    <FormattedMessage id="common.cancel" />
                  </Button>
                  <Button
                    primary
                    onClick={() =>
                      handleSave(assetClass.assetDefaultFields[fieldIndex].name)
                    }
                    size="slim"
                  >
                    <FormattedMessage id="common.save" />
                  </Button>
                </ButtonGroup>
              </CustomField>
            </Popover>
          </div>
        ) : (
          <span />
        )}
      </div>
    );
  };

  const renderRespectiveLayout = () => {
    switch (fieldObj.field.type) {
      case 'custom':
        const i = assetClass.customFields.findIndex((field) => {
          return fieldObj.field.fieldProps.label === field.label;
        });
        if (i < 0) {
          return <></>;
        }
        const fieldDetails = assetClass.customFields[i];

        if (fieldDetails.fieldType === CUSTOM_FIELDS.RICH_TEXT) {
          return (
            <div
              className={`flex fcol field-block ${
                fieldDetails.fieldType === CUSTOM_FIELDS.RICH_TEXT
                  ? 'full-height'
                  : ''
              }`}
            >
              <RichTextField
                removeFullHeightOnEditor={true}
                previewMode={true}
                isClientPortal={isClientPortal}
                label={fieldObj.field.fieldProps.label}
                value={{
                  content: getProcessedValue(
                    customFields[assetClass.customFields[i].columnName],
                    assetClass.customFields[i].columnName
                  )
                }}
                padded={false}
                saveChanges={(value) => {
                  // Save the changes to server
                  handleSave(assetClass.customFields[i].columnName, value);
                }}
                isMulti={true}
                allowItDocumentRichTextEdit={allowItDocumentRichTextEdit}
              />
            </div>
          );
        } else {
          return (
            <div className="flex fcol field-block">
              <TextStyle weight="Medium">
                {fieldObj.field.fieldProps.label}
              </TextStyle>
              <div className="flex-row flex-align-center">
                {assetClass.customFields[i]?.fieldType ===
                  CUSTOM_FIELDS.secureText ||
                assetClass.customFields[i]?.fieldType === CUSTOM_FIELDS.totp ? (
                  <>
                    <SecureText
                      module={entityName}
                      defaultValue={
                        updatedCustomFields[
                          assetClass.customFields[i]?.columnName
                        ]
                      }
                      field={assetClass.customFields[i]}
                      value={
                        updatedCustomFields[
                          assetClass.customFields[i]?.columnName
                        ]
                      }
                      updateSelection={(val) => {
                        handleSave(assetClass.customFields[i].columnName, val);
                        // handleChange(assetClass.customFields[i]?.columnName, val);
                      }}
                      entityId={props.assetId}
                      inlineEdit
                      editable={allowItDocumentRichTextEdit}
                      timer={
                        assetClass.customFields[i]?.fieldType ===
                        CUSTOM_FIELDS.totp
                      }
                      isTotp={
                        assetClass.customFields[i]?.fieldType ===
                        CUSTOM_FIELDS.totp
                      }
                    />
                  </>
                ) : (
                  <>
                    <div className="flex-row flex-align-center">
                      {!isEmpty(
                        customFields[
                          assetClass.customFields[i].columnName
                        ]?.toString()
                      )
                        ? getProcessedValue(
                            customFields[assetClass.customFields[i].columnName],
                            assetClass.customFields[i].columnName
                          )
                        : 'NA'}
                    </div>
                    {allowItDocumentRichTextEdit && (
                      <div className="popover-parent">
                        <Popover activator={activator} active={popoverActive}>
                          <CustomField>
                            <ComponentResolver
                              attribute={assetClass.customFields[i]}
                              handleChange={(value) => {
                                handleChange(
                                  assetClass.customFields[i].columnName,
                                  value
                                );
                              }}
                              value={
                                customFields[
                                  assetClass.customFields[i].columnName
                                ]
                              }
                            ></ComponentResolver>
                            <ButtonGroup spacing="tight">
                              <Button
                                id={'nonMonitorAssetCCancel'}
                                onClick={togglePopoverActive}
                                size="slim"
                              >
                                <FormattedMessage id="common.cancel" />
                              </Button>
                              <Button
                                id={'nonMonitorAssetSave'}
                                primary
                                onClick={() =>
                                  handleSave(
                                    assetClass.customFields[i].columnName
                                  )
                                }
                                size="slim"
                              >
                                <FormattedMessage id="common.save" />
                              </Button>
                            </ButtonGroup>
                          </CustomField>
                        </Popover>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        }

      case 'assetDefault':
        const fieldIndex = assetClass.assetDefaultFields.findIndex((field) => {
          return fieldObj.field.fieldProps.id === field.id;
        });
        return assetClass.assetDefaultFields[fieldIndex]?.fieldType ===
          'SECURE_TEXT' ? (
          <div className="flex fcol field-block">
            <TextStyle weight="Medium">
              {fieldObj.field.fieldProps.label}
            </TextStyle>
            <SecureText
              module={entityName}
              defaultValue={
                updatedCustomFields[
                  assetClass.assetDefaultFields[fieldIndex]?.name
                ]
              }
              field={assetClass.assetDefaultFields[fieldIndex]}
              value={
                updatedCustomFields[
                  assetClass.assetDefaultFields[fieldIndex]?.name
                ]
              }
              updateSelection={(val) => {
                handleSave(assetClass.assetDefaultFields[fieldIndex].name, val);
                // handleChange(assetClass.customFields[i]?.name, val);
              }}
              entityId={props.assetId}
              inlineEdit
              editable={allowItDocumentRichTextEdit}
            />
          </div>
        ) : (
          <div className="flex fcol field-block">
            <TextStyle weight="Medium">
              {fieldObj.field.fieldProps.label}
            </TextStyle>
            <div>
              {assetClass.assetDefaultFields[fieldIndex]?.isDynamic === true
                ? renderDynamicQueryFields(
                    assetClass.assetDefaultFields[fieldIndex]
                  )
                : renderAssetDefaultField(fieldIndex)}
            </div>
          </div>
        );

      case 'section':
        return (
          <div className="flex fcol bold">
            <Heading element="h3" weight="Bold">
              {fieldObj.field.fieldProps.label}
            </Heading>
          </div>
        );
      case 'sectionEnd':
        return <div className="divider" />;
      default:
        break;
    }
  };

  return (
    <NonMonitoredAssetInfoParent fullHeight={isRichTextEditorField}>
      <div className={isRichTextEditorField ? 'full-height' : ''}>
        {renderRespectiveLayout()}
      </div>
    </NonMonitoredAssetInfoParent>
  );
}

export default withRouter(NonMonitoredAssetInfo);
